/**
 * Template collection management utilities.
 *
 * Pure functions for manipulating template lists — sorting, filtering,
 * normalizing, merging, promoting, etc.
 *
 * Extracted from EditorApp.tsx and shared by UnifiedLeftSidebar.
 */
import type {
  TemplateCollections,
  TemplateListItem,
  TemplateStatus,
  TemplateVisibility,
} from '../types/editor';
import { resolveTemplateFolderFields } from '@/utils/templateFolders';

type TemplateCategoryKey = keyof TemplateCollections;

export const visibilityToCategoryKey = (visibility?: string | null): TemplateCategoryKey => {
  if (visibility === 'snaplayer') return 'snaplayerTemplates';
  if (visibility === 'community') return 'communityTemplates';
  return 'myTemplates';
};

export const findTemplateInCollections = (
  collections: TemplateCollections,
  templateId: string,
): TemplateListItem | null => {
  const allLists: TemplateListItem[][] = [
    collections.myTemplates,
    collections.snaplayerTemplates,
    collections.communityTemplates,
  ];
  for (const list of allLists) {
    const match = list.find((item) => item.id === templateId);
    if (match) {
      return match;
    }
  }
  return null;
};

export const normalizeTemplateListItem = (
  raw: any,
  options: { previous?: TemplateListItem | null; pendingPreview?: string | null } = {},
): TemplateListItem | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const id = typeof raw.id === 'string' ? raw.id : null;
  if (!id) {
    return null;
  }

  const previous = options.previous ?? null;
  const rawVisibility =
    typeof raw.visibility === 'string'
      ? raw.visibility
      : previous?.visibility ?? 'private';
  const visibility: TemplateVisibility =
    rawVisibility === 'snaplayer' || rawVisibility === 'community'
      ? rawVisibility
      : 'private';

  const rawStatus =
    typeof raw.status === 'string'
      ? raw.status
      : previous?.status ?? 'draft';
  const status: TemplateStatus =
    rawStatus === 'active' ? 'active' : 'draft';

  const activatedAt =
    typeof raw.activated_at === 'string'
      ? raw.activated_at
      : previous?.activated_at ?? null;

  const snapPageEnabled =
    typeof raw.snap_page_enabled === 'boolean'
      ? raw.snap_page_enabled
      : previous?.snap_page_enabled ?? false;

  const embrfyrModules = Array.isArray(raw.embrfyr_modules)
    ? raw.embrfyr_modules.map((value: unknown) => String(value || '').trim()).filter(Boolean)
    : previous?.embrfyr_modules ?? [];

  const sharedWithEmbrfyr =
    typeof raw.shared_with_embrfyr === 'boolean'
      ? raw.shared_with_embrfyr
      : previous?.shared_with_embrfyr ?? embrfyrModules.length > 0;

  const name =
    typeof raw.name === 'string' && raw.name.trim().length
      ? raw.name.trim()
      : previous?.name ?? 'Untitled Template';

  const description =
    typeof raw.description === 'string'
      ? raw.description
      : previous?.description ?? '';

  const coverUrl =
    typeof raw.cover_url === 'string' && raw.cover_url.trim().length
      ? raw.cover_url.trim()
      : previous?.cover_url ?? null;

  const createdAt =
    typeof raw.created_at === 'string'
      ? raw.created_at
      : previous?.created_at ?? null;

  const updatedAt =
    typeof raw.updated_at === 'string'
      ? raw.updated_at
      : previous?.updated_at ?? createdAt;

  let previewUrl = previous?.preview_url ?? null;
  if (typeof raw.preview_url === 'string' && raw.preview_url.trim().length) {
    previewUrl = raw.preview_url.trim();
  } else if (typeof raw.thumbnail_url === 'string' && raw.thumbnail_url.trim().length) {
    previewUrl = raw.thumbnail_url.trim();
  } else if (!previewUrl && coverUrl) {
    previewUrl = coverUrl;
  }

  let localPreview = previous?.local_preview ?? null;
  if (options.pendingPreview) {
    localPreview = options.pendingPreview;
    if (!previewUrl) {
      previewUrl = options.pendingPreview;
    }
  }

  const folderFields = resolveTemplateFolderFields(raw, previous);

  return {
    id,
    name,
    description,
    cover_url: coverUrl ?? null,
    preview_url: previewUrl ?? null,
    local_preview: localPreview,
    folder_id: folderFields.folder_id,
    folder_name: folderFields.folder_name,
    folder_sort_order: folderFields.folder_sort_order,
    visibility,
    status,
    created_at: createdAt,
    updated_at: updatedAt,
    activated_at: activatedAt,
    snap_page_enabled: snapPageEnabled,
    shared_with_embrfyr: sharedWithEmbrfyr,
    embrfyr_modules: embrfyrModules,
  };
};

export const extractTemplateTimestamp = (item: TemplateListItem | null | undefined): number => {
  const source = item?.updated_at ?? item?.created_at;
  if (!source) {
    return 0;
  }
  const parsed = Date.parse(source);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const sortTemplatesByRecency = (list: TemplateListItem[]): TemplateListItem[] => {
  const compare = (left: TemplateListItem, right: TemplateListItem): number => {
    const diff = extractTemplateTimestamp(right) - extractTemplateTimestamp(left);
    if (diff !== 0) {
      return diff;
    }

    const leftName = typeof left.name === 'string' ? left.name.toLowerCase() : '';
    const rightName = typeof right.name === 'string' ? right.name.toLowerCase() : '';
    return leftName.localeCompare(rightName);
  };

  return [...list].sort(compare);
};

export const mergeTemplateIntoCollections = (
  collections: TemplateCollections,
  template: TemplateListItem,
): TemplateCollections => {
  const category = visibilityToCategoryKey(template.visibility);
  const next: TemplateCollections = {
    myTemplates: collections.myTemplates.filter((item) => item.id !== template.id),
    snaplayerTemplates: collections.snaplayerTemplates.filter((item) => item.id !== template.id),
    communityTemplates: collections.communityTemplates.filter((item) => item.id !== template.id),
  };
  next[category] = sortTemplatesByRecency([template, ...next[category]]);
  return next;
};

export const insertTemplateAfter = (
  collections: TemplateCollections,
  template: TemplateListItem,
  afterTemplateId: string | null,
): TemplateCollections => {
  if (!afterTemplateId) {
    return mergeTemplateIntoCollections(collections, template);
  }
  const category = visibilityToCategoryKey(template.visibility);
  const next: TemplateCollections = {
    myTemplates: collections.myTemplates.filter((item) => item.id !== template.id),
    snaplayerTemplates: collections.snaplayerTemplates.filter((item) => item.id !== template.id),
    communityTemplates: collections.communityTemplates.filter((item) => item.id !== template.id),
  };
  const list = next[category];
  const index = list.findIndex((item) => item.id === afterTemplateId);
  if (index === -1) {
    next[category] = [template, ...list];
  } else {
    const updated = [...list];
    updated.splice(index + 1, 0, template);
    next[category] = updated;
  }
  return next;
};

export const removeTemplateFromCollections = (
  collections: TemplateCollections,
  templateId: string,
): TemplateCollections => ({
  myTemplates: collections.myTemplates.filter((item) => item.id !== templateId),
  snaplayerTemplates: collections.snaplayerTemplates.filter((item) => item.id !== templateId),
  communityTemplates: collections.communityTemplates.filter((item) => item.id !== templateId),
});

export const promoteTemplateInCollections = (
  collections: TemplateCollections,
  templateId: string,
): TemplateCollections => {
  if (!templateId) {
    return collections;
  }

  const promote = (list: TemplateListItem[]): TemplateListItem[] => {
    const index = list.findIndex((item) => item.id === templateId);
    if (index <= 0) {
      return list;
    }
    const clone = [...list];
    const [entry] = clone.splice(index, 1);
    const promotedEntry = {
      ...entry,
      updated_at: new Date().toISOString(),
    };
    return [promotedEntry, ...clone];
  };

  return {
    myTemplates: promote(collections.myTemplates),
    snaplayerTemplates: promote(collections.snaplayerTemplates),
    communityTemplates: promote(collections.communityTemplates),
  };
};

export const mapServerTemplatesToList = (
  rawList: unknown,
  previousList: TemplateListItem[],
): TemplateListItem[] => {
  if (!Array.isArray(rawList)) {
    return [];
  }
  const normalizedList = rawList
    .map((raw) => {
      const templateId = typeof raw?.id === 'string' ? raw.id : null;
      const previousEntry = templateId
        ? previousList.find((item) => item.id === templateId) ?? null
        : null;
      return normalizeTemplateListItem(raw, { previous: previousEntry });
    })
    .filter((item): item is TemplateListItem => !!item);

  return sortTemplatesByRecency(normalizedList);
};

export const normalizeSnapVariables = (raw: unknown): Record<string, string> => {
  if (!raw || typeof raw !== 'object') return {};
  const result: Record<string, string> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (typeof key === 'string') {
      result[key] = value == null ? '' : String(value);
    }
  });
  return result;
};
